import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authenticated
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/session");
      setUser(data?.user || false);
    } catch (err) {
      // Network error or true server failure — log so unexpected issues surface.
      console.debug("auth session check failed:", err?.response?.status, err?.message);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const register = useCallback(async (email, password, name, isBreeder = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name, is_breeder: !!isBreeder,
      });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    try {
      const { data } = await api.put("/auth/profile", patch);
      setUser(data);
      return data;
    } catch (e) {
      console.warn("updateProfile failed:", e?.message || e);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("logout request failed:", err?.message || err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, error, refresh, updateProfile }),
    [user, login, register, logout, error, refresh, updateProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
